import { useState } from "preact/hooks";
import { RANGES, settings, updateSettings } from "../../../settings";
import type { Settings } from "../../../settings";
import { cx } from "../../cx";
import { Segmented } from "../../components/Segmented";
import type { SegmentedOption } from "../../components/Segmented";
import { Slider } from "../../components/Slider";
import { TextInput } from "../../components/Field";
import { Toggle } from "../../components/Toggle";
import { CrosshairPreview } from "./CrosshairPreview";

type CrosshairSettings = Settings["crosshair"];

const STYLE_OPTIONS: readonly SegmentedOption<CrosshairSettings["style"]>[] = [
  { value: "cross", label: "Cross" },
  { value: "dot", label: "Dot" },
  { value: "circle", label: "Circle" },
  { value: "crossdot", label: "Cross + dot" },
];

const DOT_COLOR_OPTIONS: readonly SegmentedOption<CrosshairSettings["dotColor"]>[] = [
  { value: "red", label: "Red" },
  { value: "crosshair", label: "Crosshair color" },
];

const PRESET_COLORS: readonly string[] = [
  "#ffffff",
  "#3cff3c",
  "#ffe600",
  "#00e5ff",
  "#e0362c",
  "#ff3df2",
  "#ff9500",
  "#14161a",
];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** "#rrggbb" (lower case) for "rrggbb" or "#rrggbb" in any case, else null. */
function parseHex(text: string): string | null {
  const t = text.trim();
  const hex = t.startsWith("#") ? t : `#${t}`;
  return HEX_COLOR.test(hex) ? hex.toLowerCase() : null;
}

export function CrosshairTab() {
  const c = settings.value.crosshair;
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const set = (patch: Partial<CrosshairSettings>) => updateSettings({ crosshair: patch });
  const hexInvalid = hexDraft !== null && parseHex(hexDraft) === null;
  const noArms = c.style === "dot";

  const onHexInput = (text: string) => {
    setHexDraft(text);
    const color = parseHex(text);
    if (color !== null) set({ color });
  };

  return (
    <div class="settings__split">
      <div class="settings__content">
        <h3 class="settings__group">Shape</h3>
        <div class="settings__rows">
          <Segmented
            label="Style"
            value={c.style}
            options={STYLE_OPTIONS}
            onChange={(style) => set({ style })}
          />
          <Slider
            label="Size"
            description="Length of each arm. The circle adds it to its radius."
            unit="px"
            {...RANGES.crosshairSize}
            value={c.size}
            disabled={noArms}
            onChange={(size) => set({ size })}
          />
          <Slider
            label="Gap"
            description="Distance of the arms from the center."
            unit="px"
            {...RANGES.crosshairGap}
            value={c.gap}
            disabled={noArms}
            onChange={(gap) => set({ gap })}
          />
          <Slider
            label="Thickness"
            unit="px"
            {...RANGES.crosshairThickness}
            value={c.thickness}
            onChange={(thickness) => set({ thickness })}
          />
          <Toggle
            label="Dynamic"
            description="Opens the crosshair while moving and shooting."
            checked={c.dynamic}
            onChange={(dynamic) => set({ dynamic })}
          />
        </div>

        <h3 class="settings__group">Color</h3>
        <div class="settings__rows">
          <Segmented
            label="Dot sight color"
            value={c.dotColor}
            options={DOT_COLOR_OPTIONS}
            onChange={(dotColor) => set({ dotColor })}
          />
          <div class="settings__colorrow">
            <div class="settings__rowtext">
              <span class="settings__rowlabel">Color</span>
              <span class="settings__rowdesc">Pick one, type a hex value or use a preset.</span>
            </div>
            <div class="settings__colorctl">
              <div class="settings__colorinputs">
                <input
                  class="settings__colorpicker"
                  type="color"
                  value={c.color}
                  aria-label="Crosshair color picker"
                  data-nav=""
                  onInput={(e) => set({ color: e.currentTarget.value })}
                />
                <div class="settings__hexwrap" onFocusOut={() => setHexDraft(null)}>
                  <TextInput
                    class="settings__hex"
                    value={hexDraft ?? c.color}
                    onValue={onHexInput}
                    maxLength={7}
                    ariaLabel="Crosshair color as hex"
                    invalid={hexInvalid}
                  />
                </div>
              </div>
              <div class="settings__swatches" role="group" aria-label="Color presets">
                {PRESET_COLORS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    class={cx("settings__swatch", c.color === preset && "is-selected")}
                    style={{ "--swatch": preset }}
                    aria-label={preset}
                    aria-pressed={c.color === preset}
                    data-nav=""
                    onClick={() => set({ color: preset })}
                  />
                ))}
              </div>
              <span class="settings__hint" role={hexInvalid ? "alert" : undefined}>
                {hexInvalid ? "Use the form #rrggbb." : ""}
              </span>
            </div>
          </div>
          <Slider
            label="Opacity"
            {...RANGES.crosshairOpacity}
            value={c.opacity}
            onChange={(opacity) => set({ opacity })}
          />
        </div>

        <h3 class="settings__group">Outline</h3>
        <div class="settings__rows">
          <Toggle
            label="Outline"
            description="Dark border that keeps the crosshair readable on any background."
            checked={c.outline}
            onChange={(outline) => set({ outline })}
          />
          <Slider
            label="Outline thickness"
            unit="px"
            {...RANGES.crosshairOutlineThickness}
            value={c.outlineThickness}
            disabled={!c.outline}
            onChange={(outlineThickness) => set({ outlineThickness })}
          />
        </div>
      </div>

      <aside class="settings__aside" aria-label="Crosshair preview">
        <CrosshairPreview />
      </aside>
    </div>
  );
}
